import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Eye,
  EyeOff,
  Image as ImageIcon,
  Loader2,
  Plus,
  Save,
  X,
} from "lucide-react";
import React, { FC, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from "../../components/ui/Dropzone";
import { Input } from "../../components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useCategory } from "../../hooks/useCategory";
import { useCloudflareUpload } from "../../hooks/useCloudflareUpload";

interface FormData {
  name: string; // maps to Product.title
  subtitle?: string;
  description: string;
  hide: boolean; // maps to Product.status
  images: string[];
  imageUrl: string;
  imageUrlOnPage?: string;
  categoryId: string;
  vendor?: string;
  handle?: string;
  status?: "DRAFT" | "ACTIVE" | "ARCHIVED";
  tags?: string[];
  freeShippingEnabled?: boolean;
  freeShippingMinQty?: string | null;
}

export const AddProductPannel: FC<{
  onSuccess?: () => void;
  onCancel?: () => void;
}> = ({ onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const slugify = (value: string) =>
    value
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");
  const [formData, setFormData] = React.useState<FormData>({
    name: "",
    subtitle: "",
    description: "",
    hide: false,
    images: [] as string[],
    imageUrl: "",
    imageUrlOnPage: "",
    categoryId: "",
    vendor: "",
    handle: "",
    status: "ACTIVE",
    tags: [],
    freeShippingEnabled: false,
    freeShippingMinQty: "",
  });

  const [tagInput, setTagInput] = useState("");
  const [handleTouched, setHandleTouched] = React.useState(false);

  const [showCoverDropzone, setShowCoverDropzone] = useState(true);
  const [showGalleryDropzone, setShowGalleryDropzone] = useState(false);

  // COVER uploader (single)
  const coverUpload = useCloudflareUpload({
    maxFiles: 1,
    allowedMimeTypes: ["image/*"],
  });

  // GALLERY uploader (multiple)
  const galleryUpload = useCloudflareUpload({
    maxFiles: 10,
    allowedMimeTypes: ["image/*"],
  });

  // Fetch categories using the useCategory hook
  const { categories, isLoading: categoriesLoading } = useCategory({
    fetchOnMount: true,
  });

  // Create product mutation
  const createProduct = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await fetchWithAuth("/api/admin/products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: data.name.trim(),
          subtitle: data.subtitle?.trim() || undefined,
          description: data.description?.trim() || undefined,
          vendor: data.vendor?.trim() || undefined,
          handle: data.handle?.trim() || undefined,
          status: data.status || "ACTIVE",
          tags: Array.isArray(data.tags) ? data.tags : undefined,
          categories: data.categoryId ? [Number(data.categoryId)] : undefined,
          images:
            Array.isArray(data.images) && data.images.length > 0
              ? data.images
              : undefined,
          freeShippingEnabled:
            typeof data.freeShippingEnabled === "boolean"
              ? data.freeShippingEnabled
              : undefined,
          freeShippingMinQty:
            data.freeShippingMinQty === "" || data.freeShippingMinQty == null
              ? null
              : Number(data.freeShippingMinQty),
        }),
      });
      if (!response.ok) throw new Error("Eroare la crearea produsului");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Produs creat cu succes");
      setFormData({
        name: "",
        subtitle: "",
        description: "",
        hide: false,
        images: [] as string[],
        imageUrl: "",
        imageUrlOnPage: "",
        categoryId: "",
        vendor: "",
        handle: "",
        status: "ACTIVE",
        tags: [],
        freeShippingEnabled: false,
        freeShippingMinQty: "",
      });
      // Reset upload states
      coverUpload.setFiles([]);
      coverUpload.setErrors([]);
      galleryUpload.setFiles([]);
      galleryUpload.setErrors([]);
      setShowCoverDropzone(true);
      setShowGalleryDropzone(false);

      if (onSuccess) onSuccess();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Handle cover image upload results
  useEffect(() => {
    if (coverUpload.isSuccess && coverUpload.uploadedResults.length > 0) {
      const coverUrl = coverUpload.uploadedResults[0].publicUrl;
      setFormData((prev) => ({ ...prev, imageUrl: coverUrl }));
      setShowCoverDropzone(false);
    }
  }, [coverUpload.isSuccess, coverUpload.uploadedResults]);

  // Handle gallery images upload results
  useEffect(() => {
    if (galleryUpload.isSuccess && galleryUpload.uploadedResults.length > 0) {
      const newUrls = galleryUpload.uploadedResults.map((r) => r.publicUrl);
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...newUrls],
      }));
      setShowGalleryDropzone(false);
    }
  }, [galleryUpload.isSuccess, galleryUpload.uploadedResults]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createProduct.mutate(formData);
  };

  return (
    <Card className=" shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Adaugă Produs Nou
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Nume *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => {
                  const nextName = e.target.value;
                  setFormData((f) => ({
                    ...f,
                    name: nextName,
                    handle: handleTouched ? f.handle : slugify(nextName),
                  }));
                }}
                placeholder="Nume produs"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Subtitlu
              </label>
              <Input
                value={formData.subtitle || ""}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, subtitle: e.target.value }))
                }
                placeholder="Subtitlu (opțional)"
              />
            </div>
            {/* AWB Code removed */}
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Furnizor
              </label>
              <Input
                value={formData.vendor || ""}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, vendor: e.target.value }))
                }
                placeholder="Numele furnizorului (opțional)"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                URL Handle
              </label>
              <Input
                value={formData.handle || ""}
                onChange={(e) => {
                  setHandleTouched(true);
                  const slug = slugify(e.target.value);
                  setFormData((f) => ({ ...f, handle: slug }));
                }}
                placeholder="ex: titlu-produs"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-muted-foreground">
                Descriere *
              </label>
              <textarea
                value={formData.description}
                className="min-h-[120px] max-h-[300px] resize-y w-full bg-background border-2 border-gray-300 rounded-md p-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    description: e.target.value,
                  }))
                }
                placeholder="Descriere produs&#10;&#10;Folosește Enter pentru o nouă linie&#10;Exemplu:&#10;- Carte de spiritualitate&#10;- Autor: Nume Autor&#10;- Anul publicării: 2024"
                required
                rows={5}
              />
            </div>
            {/* Price and Stock moved to variants in the new model */}
            {/* Numărul paginilor removed */}
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Categorie
              </label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) =>
                  setFormData((f) => ({ ...f, categoryId: value }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selectează o categorie" />
                </SelectTrigger>
                <SelectContent>
                  {categoriesLoading ? (
                    <div className="flex items-center justify-center p-2">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span>Se încarcă...</span>
                    </div>
                  ) : categories && categories.length > 0 ? (
                    categories.map((category) => (
                      <SelectItem
                        key={category.id}
                        value={category.id.toString()}
                      >
                        {category.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="flex items-center justify-center p-2 text-muted-foreground">
                      Nu există categorii
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-muted-foreground">
                Taguri
              </label>
              <div className="flex flex-wrap gap-2 mt-2">
                {(formData.tags || []).map((tag, idx) => (
                  <span
                    key={`${tag}-${idx}`}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      className="ml-2 opacity-60 hover:opacity-100"
                      onClick={() =>
                        setFormData((f) => ({
                          ...f,
                          tags: (f.tags || []).filter(
                            (t, i) => !(t === tag && i === idx)
                          ),
                        }))
                      }
                      aria-label={`Remove ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <Input
                className="mt-2"
                placeholder="Scrie un tag și apasă Enter sau ,"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    const raw = tagInput.replace(",", " ").trim();
                    if (!raw) return;
                    const parts = raw
                      .split(/\s|,/) // split by spaces or commas
                      .map((p) => p.trim())
                      .filter(Boolean);
                    setFormData((f) => ({
                      ...f,
                      tags: Array.from(new Set([...(f.tags || []), ...parts])),
                    }));
                    setTagInput("");
                  }
                }}
                onBlur={() => {
                  const raw = tagInput.trim();
                  if (!raw) return;
                  setFormData((f) => ({
                    ...f,
                    tags: Array.from(new Set([...(f.tags || []), raw])),
                  }));
                  setTagInput("");
                }}
              />
            </div>
          </div>

          {/* Visibility Toggle */}
          <div className="flex items-center gap-3 p-4 bg-background rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.hide}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, hide: e.target.checked }))
                }
                className="w-4 h-4 text-blue-600 bg-background border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex items-center gap-2">
                {formData.hide ? (
                  <EyeOff className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <Eye className="w-4 h-4 text-green-600" />
                )}
                <span className="text-sm font-medium text-muted-foreground">
                  {formData.hide ? "Produs ascuns" : "Produs vizibil"}
                </span>
              </div>
            </label>
            <span className="text-xs text-muted-foreground">
              Produsele ascunse nu vor fi vizibile pe site-ul public
            </span>
          </div>

          {/* Free Shipping */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-2 p-3 rounded-md border bg-muted/20">
              <input
                id="freeShippingEnabled"
                type="checkbox"
                checked={!!formData.freeShippingEnabled}
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    freeShippingEnabled: e.target.checked,
                  }))
                }
                className="w-4 h-4"
              />
              <label htmlFor="freeShippingEnabled" className="text-sm">
                Activează livrare gratuită
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-muted-foreground">
                Livrare gratuită de la N bucăți
              </label>
              <Input
                type="number"
                min="1"
                step="1"
                value={formData.freeShippingMinQty ?? ""}
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    freeShippingMinQty: e.target.value,
                  }))
                }
                placeholder="Ex: 3"
                disabled={!formData.freeShippingEnabled}
              />
            </div>
          </div>

          {/* Cover Image Upload */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Imagine Principală
            </label>

            {formData.imageUrl && !showCoverDropzone ? (
              <div className="flex flex-col items-center">
                <div className="relative rounded-md overflow-hidden w-full max-w-md h-64 bg-gray-100">
                  <img
                    src={formData.imageUrl}
                    alt="Cover"
                    className="w-full h-full object-contain"
                  />
                  <Button
                    className="absolute top-2 right-2 bg-white text-muted-foreground hover:bg-gray-100 border shadow"
                    size="sm"
                    onClick={() => setShowCoverDropzone(true)}
                  >
                    Schimbă
                  </Button>
                </div>
              </div>
            ) : (
              <Dropzone {...coverUpload}>
                <DropzoneEmptyState />
                <DropzoneContent />
              </Dropzone>
            )}

            {/* Legacy URL input option */}
            {!formData.imageUrl && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-2">
                  Sau introdu manual URL-ul imaginii:
                </p>
                <Input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, imageUrl: e.target.value }))
                  }
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            )}
          </div>

          {/* Gallery Images Upload */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Imagini Suplimentare
            </label>

            {/* Display existing gallery images */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {formData.images.map((url, index) => (
                  <div
                    key={index}
                    className="relative group rounded-md overflow-hidden bg-gray-50 border"
                  >
                    <img
                      src={url}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-40 object-cover"
                    />
                    <button
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-white rounded p-1 border shadow"
                      onClick={() => {
                        const newImages = formData.images.filter(
                          (_, i) => i !== index
                        );
                        setFormData((f) => ({ ...f, images: newImages }));
                      }}
                      title="Șterge"
                    >
                      <X className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Gallery dropzone */}
            {showGalleryDropzone ? (
              <Dropzone {...galleryUpload}>
                <DropzoneEmptyState />
                <DropzoneContent />
              </Dropzone>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowGalleryDropzone(true)}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Adaugă Imagini
              </Button>
            )}

            {/* Legacy URL input option */}
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-2">
                Sau introdu manual URL-ul unei imagini:
              </p>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={""}
                  onChange={(e) => {
                    if (e.target.value) {
                      setFormData((f) => ({
                        ...f,
                        images: [...f.images, e.target.value],
                      }));
                      e.target.value = "";
                    }
                  }}
                  placeholder="https://example.com/image.jpg"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const input = document.querySelector(
                      'input[type="url"]:not([value])'
                    ) as HTMLInputElement;
                    if (input && input.value) {
                      setFormData((f) => ({
                        ...f,
                        images: [...f.images, input.value],
                      }));
                      input.value = "";
                    }
                  }}
                >
                  Adaugă
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" variant="outline" onClick={onCancel}>
              Anulează
            </Button>
            <Button
              type="submit"
              disabled={createProduct.isPending}
              className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
            >
              {createProduct.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Se salvează...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Salvează
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
